import React, {useEffect, useMemo, useState} from 'react'
import {Box, Spinner, Text} from 'grommet'
import {ButtonProps, Input, Select} from "antd";
import {useAccount, useSendTransaction, useWaitForTransaction, useContractWrite, erc20ABI, useBalance} from "wagmi";
import {GradientFilledButton} from "../../components/button";
import {toast} from "react-toastify";
import {TokensList} from "../../tokens";
import {Currency} from "@uniswap/sdk-core";
import {formatUnits, parseUnits} from "viem";
import {getBalance} from "../../api/erc20";

const tokensOptions = TokensList.map((item) => {
  return {
    value: item.symbol,
    label: item.symbol
  }
})

export const SendContent = () => {
  const { address: userAddress } = useAccount()
  const { sendTransactionAsync } = useSendTransaction()

  const [selectedToken, setSelectedToken] = useState<Currency>(TokensList[0])
  const [destinationAddress, setDestinationAddress] = useState('')
  const [amount, setAmount] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [txHash, setTxHash] = useState<string>('')

  const { data, writeAsync } = useContractWrite({
    address: undefined,
  })

  const { data: nativeBalance } = useBalance({
    address: userAddress,
    enabled: !!userAddress,
    watch: true
  })
  const [selectedTokenBalance, setSelectedTokenBalance] = useState<bigint>(0n)

  const {
    isSuccess: isSendSuccess,
    isFetching: isSendFetching
  } = useWaitForTransaction({
    hash: txHash as `0x${string}`,
    enabled: !!txHash,
    confirmations: 1
  })

  useEffect(() => {
    if(isSendSuccess && txHash) {
      toast.success(`${amount} ${selectedToken.symbol} successfully sent! Txn hash: ${txHash}.`, {
        toastId: txHash
      })
      setAmount('')
    }
  }, [isSendSuccess, txHash])

  useEffect(() => {
    const updateERC20Balance = async () => {
      try {
        if(selectedToken.isToken && userAddress) {
          const data = await getBalance(selectedToken.address, userAddress)
          setSelectedTokenBalance(data)
        }
      } catch (e) {
        console.error('Failed to fetch erc20 balance:', e)
      }
    }

    updateERC20Balance()
  }, [selectedToken.isToken]);

  const onSendClicked = async () => {
    try {
      setLoading(true)
      if(selectedToken.isNative) {
        const data = await sendTransactionAsync({
          to: destinationAddress,
          value: parseUnits(amount, 18),
        })
        setTxHash(data.hash)
        console.log('Tx send result:', data)
      } else {
        const data = await writeAsync({
          // @ts-ignore
          address: selectedToken.address as `0x${string}`,
          abi: erc20ABI,
          functionName: 'transfer',
          args: [destinationAddress,  parseUnits(amount, selectedToken.decimals)]
        })
        console.log('Tx send result:', data)
        setTxHash(data.hash)
      }
    } catch(e) {
      console.error('Failed to send transaction:', e)
      toast.error(`Failed to send transaction. Try again later.`)
    } finally {
      setLoading(false)
    }
  }

  const loading = isLoading || isSendFetching
  const disabled = isSendFetching || isLoading || !selectedToken

  const buttonProps: ButtonProps = {
    disabled,
    style: {
      height: '40px',
      opacity: (disabled || loading) ? 0.4 : 1
    }
  }

  const tokenBalanceFormatted = useMemo(() => {
    let value = '0.00'
    if(selectedToken.isNative) {
      value = nativeBalance?.formatted || '0'
    } else {
      value = formatUnits(selectedTokenBalance, selectedToken.decimals)
    }
    return (+(value || '0')).toFixed(4)
  }, [selectedToken.isNative, selectedToken.decimals, nativeBalance?.formatted, selectedTokenBalance])

  return <Box gap={'16px'}>
    <Box gap={'8px'}>
      <Text size={'large'}>Send from</Text>
      <Select
        defaultValue={userAddress}
        disabled={true}
        size={'large'}
        // style={{ width: 120 }}
        onChange={() => {}}
        options={[
          { value: userAddress, label: userAddress },
        ]}
      />
    </Box>
    <Box gap={'8px'}>
      <Text size={'large'}>Send to</Text>
      <Input
        placeholder={'Enter public address (0x)'}
        size={'large'}
        value={destinationAddress}
        onChange={(e) => {
          setDestinationAddress(e.target.value)
        }}
      />
    </Box>
    <Box gap={'8px'}>
      <Text size={'large'}>Asset</Text>
      <Select
        placeholder={'Select asset'}
        defaultValue={tokensOptions.length > 0 ? tokensOptions[0].value : ''}
        size={'large'}
        // style={{ width: 120 }}
        onChange={(value) => {
          const token = TokensList.find(item => item.symbol === value)
          if(token) {
            setSelectedToken(token)
          }
        }}
        options={tokensOptions}
      />
    </Box>
    <Box gap={'8px'}>
      <Text size={'large'}>Amount</Text>
      <Input
        placeholder={'0.00'}
        value={amount}
        size={'large'}
        onChange={(e) => {
          setAmount(e.target.value)
        }}
      />
      <Box align={'end'}>
        <Box  direction={'row'} gap={'8px'}>
          <Text>
            Available Balance: {tokenBalanceFormatted}
          </Text>
          {selectedToken &&
              <Box  onClick={() => {
                const balance = selectedToken.isNative
                  ? (nativeBalance?.formatted || '0')
                  : formatUnits(selectedTokenBalance, selectedToken.decimals)
                setAmount(balance)
              }}>
                  <Text weight={600}>Max</Text>
              </Box>
          }
        </Box>
      </Box>
    </Box>
    <Box>
      <GradientFilledButton {...buttonProps} onClick={onSendClicked}>
        <Box direction={'row'} align={'center'} justify={'center'} gap={'12px'}>
          {loading &&
              <Spinner color={'white'} />
          }
          <Box>
            <Text color={'#323232'} size={'20px'} weight={500}>Send</Text>
          </Box>
        </Box>
      </GradientFilledButton>
    </Box>
  </Box>
}
